import { api } from "./utils/axios";
import { LoginCredentials, LoginResponse, User } from "@/types/User.interface";
import { userStore } from "@/stores/UserStore";
import { universalServices } from "./UniversalServices";
import { addNotification } from "@/utils/addNotification";

/* ---------------- TYPES ---------------- */

interface CaptchaData {
  image_data: string;
  phrase_array: string[];
  needed_captcha: boolean;
  captcha_id: string;
}

export interface AuthCheckResponse {
  csrf_token?: string;
  user_id?: number;
  other_users?: User[];
}

/* ---------------- SERVICE ---------------- */

class AuthService {
  private csrfToken: string | null = null;

  constructor() {
    // восстановление csrf после перезагрузки
    const savedCsrf = localStorage.getItem("csrf_token");
    if (savedCsrf) {
      this.csrfToken = savedCsrf;
    }

    this.applyAuthHeaders();
  }

  /* ---------- helpers ---------- */

  private applyAuthHeaders() {
    const apiToken = localStorage.getItem("api_token");

    if (apiToken) {
      api.defaults.headers.common["Authorization"] = `Bearer ${apiToken}`;

      if (this.csrfToken) {
        api.defaults.headers.common["X-CSRF-Token"] = this.csrfToken;
      }
    } else {
      delete api.defaults.headers.common["Authorization"];
      delete api.defaults.headers.common["X-CSRF-Token"];
    }
  }

  setCsrfToken(token: string) {
    this.csrfToken = token;
    localStorage.setItem("csrf_token", token);
    this.applyAuthHeaders();
  }

  //eslint-disable-next-line
  private setAuthData(user: any, token: string, csrf: string) {
    localStorage.setItem("user", JSON.stringify(user));
    localStorage.setItem("api_token", token);

    api.defaults.headers.common["Authorization"] = `Bearer ${token}`;
    this.setCsrfToken(csrf);
  }

  /* ---------- captcha ---------- */

  async getImage(): Promise<{
    success?: boolean;
    data?: CaptchaData;
    error?: string;
  }> {
    try {
      const response = await universalServices.getData<CaptchaData>("auth", "get-phrase");

      if (response.success && response.data) {
        return { success: true, data: response.data };
      }

      return { success: false };
    } catch {
      return { success: false, error: "Ошибка получения капчи" };
    }
  }

  /* ---------- login ---------- */

  async login(
    credentials: LoginCredentials & { captcha_id: string },
  ): Promise<{ success: boolean; data?: LoginResponse; error?: string }> {
    try {
      const response = await universalServices.getData<LoginResponse>("auth", "login", credentials);

      if (response.success && response.data) {
        const { user, token, csrf_token } = response.data;
        this.setAuthData(user, token, csrf_token);

        return { success: true, data: response.data };
      }

      return { success: false, error: response.message };
      //eslint-disable-next-line
    } catch (error: any) {
      const message =
        error instanceof Error ? error.message : typeof error === "string" ? error : "Неизвестная ошибка";
      return { success: false, error: message };
    }
  }

  /* ---------- check auth ---------- */

  async checkAuth(): Promise<{
    success: boolean;
    message?: string;
    other_users?: User[];
  }> {
    try {
      const apiToken = localStorage.getItem("api_token");
      if (!apiToken) {
        return { success: false, message: "Отсутствует токен авторизации" };
      }

      const creds = { role: userStore.role || 5 };

      const response = await universalServices.getData<AuthCheckResponse>("check-auth", "checkAuth", creds);
      console.log(response);
      if (response.success && response.data?.csrf_token) {
        this.setCsrfToken(response.data.csrf_token);
      }

      return {
        success: response.success,
        message: response.message,
        other_users: response.data?.other_users,
      };
      //eslint-disable-next-line
    } catch (error: any) {
      if (error.response?.data?.error) {
        return { success: false, message: error.response.data.error };
      }

      return { success: false, message: "Ошибка сервера" };
    }
  }

  /* ---------- logout ---------- */

  async logout(): Promise<boolean> {
    try {
      await universalServices.getData("auth", "logout");
      //eslint-disable-next-line
    } catch (e) {
      const message = e instanceof Error ? e.message : typeof e === "string" ? e : "Unknown error";
      addNotification({ message: message, type: "error" });
    }

    this.clearAuthData();
    return true;
  }

  clearAuthData() {
    localStorage.removeItem("user");
    localStorage.removeItem("api_token");
    localStorage.removeItem("csrf_token");

    this.csrfToken = null;
    delete api.defaults.headers.common["Authorization"];
    delete api.defaults.headers.common["X-CSRF-Token"];
  }

  getCsrfToken(): string | null {
    return this.csrfToken;
  }
}

export const authService = new AuthService();
